import PropTypes from 'prop-types';
import {
  memo, useCallback, useEffect, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CircularProgress,
} from '@material-ui/core';
import _ from 'lodash';
import { SearchStatus } from '../../../../../../constants';
import withSpinner from '../../../../../../HOCs/withSpinner';
import { getFlightSeatmapRequest } from '../../../../../../store/slices/flight';
import { changeTrip, updateTripStatusReset } from '../../../../../../store/slices/trip';
import SeatPicker from './SeatPicker';
import SeatMapLegend from './SeatMapLegend';
import SeatMapPassengers from './SeatMapPassengers';

const FlightSeatMap = ({
  flight, canPickSeat, onModalClose, defaultSelectedTravelerIndex,
}) => {
  const dispatch = useDispatch();
  const key = `${flight.departDateTime}/${flight.flightNumber}`;
  const seatMap = useSelector((state) => state.flight.flightSeatMaps[key]);
  const { passengers, updateTripStatus } = useSelector((state) => state.trip);
  const [selectedTravelerIndex, setSelectedTravelerIndex] = useState(defaultSelectedTravelerIndex);

  // seat which need to selected after trip updated with selected fare, and seatmap updated
  const [seatToSelectAfterTripUpdate, setSeatToSelectAfterTripUpdate] = useState(0);

  const selectTravelerSeat = useCallback((seat) => {
    dispatch(changeTrip({
      field: 'passengers',
      value: {
        ...passengers,
        [selectedTravelerIndex]: {
          ...passengers[selectedTravelerIndex],
          travelerPreferences: {
            ...passengers[selectedTravelerIndex].travelerPreferences,
            flightSeatNumbers: {
              ...((passengers[selectedTravelerIndex].travelerPreferences || {})
                .flightSeatNumbers || {}),
              [flight.flightNumber]: seat.isAlreadySelected ? null : {
                seatNumber: seat.seatNumber,
                price: seat.price,
              },
            },
          },
        },
      },
    }));
  }, [dispatch, flight.flightNumber, passengers, selectedTravelerIndex]);

  const handleSeatSelect = (seat) => {
    if (canPickSeat) {
      if (seat.doSelectSeatAfterTripUpdate) {
        setSeatToSelectAfterTripUpdate(seat);
        return;
      }

      if (passengers && passengers[selectedTravelerIndex]
        && seat.isAlreadySelected
        && passengers[selectedTravelerIndex].travelerPreferences
        && passengers[selectedTravelerIndex].travelerPreferences.flightSeatNumbers
        && passengers[selectedTravelerIndex].travelerPreferences
          .flightSeatNumbers[flight.flightNumber]
        && passengers[selectedTravelerIndex].travelerPreferences
          .flightSeatNumbers[flight.flightNumber].seatNumber !== seat.seatNumber) {
        return;
      }
      selectTravelerSeat(seat);

      let currentTravelerIndex = selectedTravelerIndex;
      if (passengers[selectedTravelerIndex + 1]
        && passengers[selectedTravelerIndex + 1].isLapInfant) {
        currentTravelerIndex += 1;
      }
      if (passengers) {
        if (currentTravelerIndex < Object.keys(passengers).length - 1) {
          setSelectedTravelerIndex(currentTravelerIndex + 1);
        } else setSelectedTravelerIndex(0);
      }
    }
  };

  const removeAllTravelersSeatSelection = () => {
    const updatedPassengers = {
      ...passengers,
    };

    Object.keys(passengers).forEach((k) => {
      updatedPassengers[k] = {
        ...updatedPassengers[k],
        travelerPreferences: {
          ...updatedPassengers[k].travelerPreferences,
          flightSeatNumbers: null,
        },
      };
    });

    dispatch(changeTrip({
      field: 'passengers',
      value: {
        ...updatedPassengers,
      },
    }));
  };

  useEffect(() => {
    dispatch(getFlightSeatmapRequest({
      flight,
      key,
    }));
  }, [dispatch, flight, key]);

  useEffect(() => {
    // We update trip after user select seat from another fare class.
    // Also, after trip updated, we update seatmap
    // After trip and seatmap updated, select the seat
    if (seatToSelectAfterTripUpdate
      && seatMap && seatMap.getFlightSeatmapStatus === SearchStatus.DONE) {
      if (seatMap.rows) {
        const seatToSelect = _.find(_.flatMap(seatMap.rows, (r) => r.seats),
          (st) => st.seatNumber === seatToSelectAfterTripUpdate.seatNumber);

        // Select seat only if it's available and price not negative
        if (seatToSelect != null
          && seatToSelect.isSeatAvailable
          && (seatToSelect.price || 0) >= 0) {
          selectTravelerSeat(seatToSelect);
        }
      }
      setSeatToSelectAfterTripUpdate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seatMap]);

  useEffect(() => {
    if (updateTripStatus === SearchStatus.DONE) {
      dispatch(updateTripStatusReset());
      removeAllTravelersSeatSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTripStatus]);

  const Body = () => {
    if (seatMap && seatMap.rows) {
      return (
        <div className="flex">
          <div className="sm:w-4/5 w-3/4 text-center overflow-x-auto">
            <SeatPicker
              seatmap={seatMap}
              flight={flight}
              canPickSeat={canPickSeat}
              onSeatSelect={handleSeatSelect}
            />
          </div>
          <div className="w-1/4 sm:w-1/5 bg-body-0 sm:px-5 px-2">
            {canPickSeat && (
              <SeatMapPassengers
                seatMap={seatMap}
                flight={flight}
                selectedTravelerIndex={selectedTravelerIndex}
                setSelectedTravelerIndex={setSelectedTravelerIndex}
              />
            )}
            {seatMap.legend && <SeatMapLegend seatMap={seatMap} />}

            <div className="mb-10">
              <button type="button" onClick={onModalClose} className="bg-button-theme text-white py-1 w-full rounded">Done</button>
            </div>
          </div>
        </div>
      );
    }
    return <div className="p-8">We currently do not offer ability to view seats/request a seat for this flight/airline. Usually you can request a seat directly with the airline after the booking. Some airlines may charge a seat assignment fee. Contact us if you need assistance.</div>;
  };
  return (
    <div className={`${seatMap && seatMap.getFlightSeatmapStatus === SearchStatus.SEARCHING ? 'py-20' : ''} relative`}>
      {updateTripStatus === SearchStatus.SEARCHING && (
        <div className="text-theme absolute w-full h-full text-center" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: '9999' }}>
          <CircularProgress color="inherit" className="mt-10" />
          <div className="font-bold">Updating fare class...</div>
        </div>
      )}
      {withSpinner(Body, (seatMap || {}).getFlightSeatmapStatus === SearchStatus.SEARCHING, 'Loading seat map...')}
    </div>
  );
};

FlightSeatMap.propTypes = {
  canPickSeat: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
  defaultSelectedTravelerIndex: PropTypes.number,
  flight: PropTypes.shape({
    carrierCode: PropTypes.string,
    departDateTime: PropTypes.string,
    flightNumber: PropTypes.string,
  }).isRequired,
};

FlightSeatMap.defaultProps = {
  defaultSelectedTravelerIndex: 0,
};

export default memo(FlightSeatMap);
